// src/components/PushNotificationSettings.jsx
import { useState, useEffect } from 'react';
import { Bell, BellOff, Smartphone, Monitor, Loader2, Send, CheckCircle, XCircle, Trash2 } from 'lucide-react';
import { usePushNotifications } from '../hooks/usePushNotifications';

export default function PushNotificationSettings() {
  const {
    isSupported,
    isSubscribed,
    permission,
    loading,
    error,
    subscribe,
    unsubscribe,
    sendTest
  } = usePushNotifications();

  const [preferences, setPreferences] = useState({
    pushEnabled: true,
    pushIntercomCalls: true,
    pushDoorEvents: false,
    pushMotionAlerts: false,
    pushSystemAlerts: true
  });
  const [devices, setDevices] = useState([]);
  const [removingDevice, setRemovingDevice] = useState(null);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [testStatus, setTestStatus] = useState(null);

  useEffect(() => {
    loadPreferences();
    loadDevices();
  }, []);

  const loadPreferences = async () => {
    try {
      const res = await fetch('/api/push/preferences', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setPreferences(data);
      }
    } catch (err) {
      console.error('Failed to load preferences:', err);
    }
  };

  const loadDevices = async () => {
    try {
      const res = await fetch('/api/push/subscriptions', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setDevices(data.subscriptions || []);
      }
    } catch (err) {
      console.error('Failed to load devices:', err);
    }
  };

  const removeDevice = async (deviceId) => {
    setRemovingDevice(deviceId);
    try {
      const res = await fetch(`/api/push/subscriptions/${deviceId}`, {
        method: "DELETE",
        credentials: "include"
      });
      if (res.ok) {
        setDevices(devices.filter(d => d.id !== deviceId));
      }
    } catch (err) {
      console.error("Failed to remove device:", err);
    } finally {
      setRemovingDevice(null);
    }
  };

  const updatePreference = async (key, value) => {
    setSavingPrefs(true);
    const updated = { ...preferences, [key]: value };
    setPreferences(updated);

    try {
      await fetch('/api/push/preferences', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ [key]: value })
      });
    } catch (err) {
      console.error('Failed to save preference:', err);
      setPreferences(preferences);
    }
    setSavingPrefs(false);
  };

  const handleToggle = async () => {
    if (isSubscribed) {
      await unsubscribe();
    } else {
      const success = await subscribe();
      if (success) {
        loadDevices();
      }
    }
  };

  const handleSendTest = async () => {
    setTestStatus('sending');
    const success = await sendTest();
    setTestStatus(success ? 'success' : 'error');
    setTimeout(() => setTestStatus(null), 3000);
  };

  const parseUserAgent = (ua) => {
    if (!ua) return { device: 'Unknown', browser: 'Unknown' };
    
    let device = 'Desktop';
    let browser = 'Unknown';
    
    if (/iPhone|iPad|iPod/.test(ua)) device = 'iOS';
    else if (/Android/.test(ua)) device = 'Android';
    else if (/Mac/.test(ua)) device = 'Mac';
    else if (/Windows/.test(ua)) device = 'Windows';
    else if (/Linux/.test(ua)) device = 'Linux';
    
    if (/Chrome/.test(ua) && !/Edge/.test(ua)) browser = 'Chrome';
    else if (/Safari/.test(ua) && !/Chrome/.test(ua)) browser = 'Safari';
    else if (/Firefox/.test(ua)) browser = 'Firefox';
    else if (/Edge/.test(ua)) browser = 'Edge';
    
    return { device, browser };
  };

  if (!isSupported) {
    return (
      <div className="info-banner warning">
        <BellOff size={20} />
        <div>
          <strong>Push Notifications Not Supported</strong>
          <p>Your browser doesn't support push notifications. Try using Chrome, Firefox, or Edge.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="push-settings">
      {/* Enable/Disable Toggle */}
      <div className="setting-row">
        <div className="setting-info">
          <Bell size={20} className="setting-icon" />
          <div>
            <span className="setting-label">Enable Push Notifications</span>
            <span className="setting-hint">
              {isSubscribed 
                ? 'Enabled on this device' 
                : permission === 'denied'
                  ? 'Blocked by browser settings'
                  : 'Receive alerts when the app is closed'}
            </span>
          </div>
        </div>
        <div className="setting-controls">
          {isSubscribed && (
            <button
              className="btn btn-secondary btn-sm"
              onClick={handleSendTest}
              disabled={testStatus === 'sending'}
              title="Send test notification"
            >
              {testStatus === 'sending' ? (
                <Loader2 size={16} className="spin" />
              ) : testStatus === 'success' ? (
                <CheckCircle size={16} className="text-success" />
              ) : testStatus === 'error' ? (
                <XCircle size={16} className="text-error" />
              ) : (
                <Send size={16} />
              )}
              Test
            </button>
          )}
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={isSubscribed}
              onChange={handleToggle}
              disabled={loading || permission === 'denied'}
            />
            <span className="toggle-slider"></span>
          </label>
        </div>
      </div>

      {error && (
        <div className="info-banner error">
          <XCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {permission === 'denied' && (
        <div className="info-banner warning">
          <BellOff size={16} />
          <span>Push notifications are blocked. Please enable them in your browser settings.</span>
        </div>
      )}

      {/* Notification Types */}
      {isSubscribed && (
        <div className="notification-types">
          <h4 className="subsection-title">Notification Types</h4>
          
          {[
            { key: 'pushIntercomCalls', label: 'Intercom Calls', desc: 'When someone rings an intercom' },
            { key: 'pushDoorEvents', label: 'Door Events', desc: 'Access granted/denied, door held open' },
            { key: 'pushMotionAlerts', label: 'Motion Alerts', desc: 'Camera motion detection alerts' },
            { key: 'pushSystemAlerts', label: 'System Alerts', desc: 'System errors and warnings' }
          ].map(({ key, label, desc }) => (
            <div key={key} className="setting-row">
              <div className="setting-info">
                <div>
                  <span className="setting-label">{label}</span>
                  <span className="setting-hint">{desc}</span>
                </div>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={preferences[key] ?? true}
                  onChange={(e) => updatePreference(key, e.target.checked)}
                  disabled={savingPrefs}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          ))}
        </div>
      )}

      {/* Registered Devices */}
      {devices.length > 0 && (
        <div className="registered-devices">
          <h4 className="subsection-title">Registered Devices ({devices.length})</h4>
          <div className="devices-list">
            {devices.map((device) => {
              const { device: deviceType, browser } = parseUserAgent(device.userAgent);
              return (
                <div key={device.id} className="device-item">
                  {deviceType === 'iOS' || deviceType === 'Android' ? (
                    <Smartphone size={18} className="device-icon" />
                  ) : (
                    <Monitor size={18} className="device-icon" />
                  )}
                  <div className="device-info">
                    <span className="device-name">{deviceType} - {browser}</span>
                    <span className="device-meta">
                      Last used: {new Date(device.lastUsedAt).toLocaleDateString()}
                    </span>
                  </div>
                  <button
                    className="btn btn-icon btn-danger-subtle"
                    onClick={() => removeDevice(device.id)}
                    disabled={removingDevice === device.id}
                    title="Remove this device"
                  >
                    {removingDevice === device.id ? (
                      <Loader2 size={16} className="spin" />
                    ) : (
                      <Trash2 size={16} />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
